import React, { useState } from "react";
import { toast } from "react-toastify";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    Typography,
    Box,
    Link
} from "@material-ui/core";

import { CloudUpload } from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { importProductsCSV } from "../../services/products";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    uploadButton: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    instructions: {
        marginBottom: theme.spacing(2),
    },
    example: {
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(1),
        borderRadius: 4,
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        marginTop: theme.spacing(1),
    }
}));

const ImportProductsModal = ({ open, onClose, reload }) => {
    const classes = useStyles();
    const [file, setFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = () => {
        setFile(null);
        onClose();
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
        }
    };

    const handleImport = async () => {
        if (!file) {
            toast.error("Selecione um arquivo CSV");
            return;
        }

        setIsSubmitting(true);
        try {
            const { data } = await importProductsCSV(file);
            toast.success(data.message || i18n.t("products.toasts.imported"));
            if (reload) reload();
            handleClose();
        } catch (err) {
            toastError(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const downloadExample = () => {
        const csvContent = "nome,valor,cor\nProduto Exemplo,99.90,#FF5733\nOutro Produto,149.90,#33FF57";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "exemplo_produtos.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{i18n.t("products.importModal.title")}</DialogTitle>
            <DialogContent dividers>
                <Box className={classes.root}>
                    <Typography variant="body2" className={classes.instructions}>
                        {i18n.t("products.importModal.instructions")}
                    </Typography>

                    <Box className={classes.example}>
                        <Typography variant="caption">
                            <strong>Formato com vírgula (,):</strong><br />
                            nome,valor,cor<br />
                            Produto 1,99.90,#FF5733<br />
                            <br />
                            <strong>Formato com ponto e vírgula (;):</strong><br />
                            nome;valor;cor<br />
                            Produto 1;99.90;#33FF57
                        </Typography>
                    </Box>

                    <Link
                        component="button"
                        variant="body2"
                        onClick={downloadExample}
                        style={{ marginTop: 10 }}
                    >
                        {i18n.t("products.importModal.downloadExample")}
                    </Link>

                    <input
                        accept=".csv"
                        style={{ display: 'none' }}
                        id="csv-upload"
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="csv-upload" style={{ width: '100%' }}>
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUpload />}
                            className={classes.uploadButton}
                            fullWidth
                        >
                            {file ? file.name : "Selecionar arquivo CSV"}
                        </Button>
                    </label>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting}
                    variant="outlined"
                >
                    {i18n.t("global.buttons.cancel")}
                </Button>
                <Button
                    onClick={handleImport}
                    color="primary"
                    disabled={isSubmitting || !file}
                    variant="contained"
                    className={classes.btnWrapper}
                >
                    Importar
                    {isSubmitting && (
                        <CircularProgress
                            size={24}
                            className={classes.buttonProgress}
                        />
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportProductsModal;
